import type {ExportInvoiceModal_Viewer$data as ExportInvoiceModal_Viewer} from './__generated__/ExportInvoiceModal_Viewer.graphql';
import {IntlShape} from 'react-intl';
import type {ExportTarget, QuickbooksCustomerOption} from './ExportInvoiceModal.target';

export interface ExportInvoiceModalProps {
	closeModal: () => void;
	viewer: ExportInvoiceModal_Viewer;
	/** Absent on the legacy path, where the client chosen in the header is the target. */
	billToId?: string;
	/** Set only on the legacy QuickBooks path (QBO flag on, invoicing_pathways off). */
	quickbooksCustomers?: QuickbooksCustomerOption[];
}

export type Invoice = NonNullable<ExportInvoiceModal_Viewer['invoice']>;

export type InvoiceEntry = NonNullable<
	NonNullable<NonNullable<NonNullable<NonNullable<ExportInvoiceModal_Viewer['invoice']>['entries']>['edges']>[0]>['node']
>;

export type InvoiceBillTo = NonNullable<ExportInvoiceModal_Viewer['billTo']>;

export type XeroAccountItem = {
	value: string;
	label: string;
	taxRate: number;
	type: string;
	code: string;
};

export type InvoiceEntryRow = {
	/**
	 * The price the invoice ENTRY carries, set once from the Relay entry and never rewritten.
	 *
	 * Picking a product overwrites `unitPrice`, so the live price stops being a usable original the
	 * moment it does; clearing the product restores this one instead of leaving the product's.
	 */
	originalUnitPrice?: number | null;
	projectColor: string;
	projectId: string;
	customProjectId: string | null | undefined;
	entryDescription: string | null;
	totalPrice?: number;
	discountPercentage?: number;
	taxPercentage?: number;
	label?: string;
	xeroAccount?: string;
	accountCode?: string;
	quickbooksItem?: string;
	quickbooksTaxable?: boolean;
	quickbooksUnitPrice?: number;
	/*
	 * The picked product's QuickBooks description, kept beside the line's own description rather
	 * than written over it, so the description column can follow the toggles (T4-149) - with the
	 * flag on or off (T4-156).
	 */
	quickbooksDescription?: string | null;
	economicProduct?: string;
	/*
	 * The Xero line selections, added by T4-132. All optional and unset on the flag-off path,
	 * so no existing consumer of this type sees a change.
	 */
	xeroItemId?: string;
	xeroItemCode?: string;
	xeroTaxRate?: number;
	xeroTaxType?: string;
	xeroTracking?: Record<string, string>;
} & InvoiceEntry;

export type ExportInvoiceOnSuccess = () => void;

export type OnErrorType = (error: Error, onFailure: (error: Error) => void) => void;

export interface ExportInvoiceParams {
	onSuccess: ExportInvoiceOnSuccess;
	intl: IntlShape;
	invoice: Invoice;
	invoiceRows: InvoiceEntryRow[];
	billTo: NonNullable<InvoiceBillTo>;
	onError: OnErrorType;
}

export interface ExportXeroInvoiceParams extends ExportInvoiceParams {
	projectInDescription: boolean;
	/**
	 * Called when the export finished and failed: stop the spinner, keep the modal open, and show
	 * the reason INSIDE it.
	 *
	 * Separate from `onSuccess`, which also closes. Closing on failure throws away every pick the
	 * user made, and the export writes nothing to the invoice unless Xero accepted it - so what they
	 * are looking at is still exactly what they would retry.
	 *
	 * It takes the reason because a `MODAL_TYPE.WARNING` cannot be seen while this modal is open:
	 * the design-system `Modal` is portalled above it and `aria-hidden`s the tree it renders into.
	 * Null when the service gave nothing a user can act on, and the modal supplies its own copy.
	 */
	onExportFailed: (reason?: string | null) => void;
	/*
	 * The gate resolved ONCE per render by the modal, passed in rather than re-evaluated here.
	 * Two independent reads of the flag store in one export can disagree if it changes
	 * mid-session, and then the payload shape does not match the table the user was looking at.
	 */
	useLinesTable: boolean;
}

export interface ExportQuickbooksInvoiceParams extends Omit<ExportInvoiceParams, 'billTo'> {
	target: ExportTarget;
	quickbookSettings: QuickbookSetting[];
	taxCode: TaxCode | null;
}

export interface TaxCode {
	value: string;
	label: string;
	taxRate: number;
}

export interface QuickbookProductService {
	value: string;
	label: string;
	// Optional: an imported item can have neither. The live list never guaranteed them either.
	description?: string;
	unitPrice?: number;
	taxable: boolean;
}

export interface EconomicProduct {
	value: string;
	label: string;
	description: string;
	unitPrice: number;
	taxable: boolean;
}

export enum QuickbooksSettingId {
	CONTROL_INVOICE_REFERENCE,
	USE_PRODUCT_SERVICE_DESCRIPTIONS,
	INCLUDE_PROJECT_NAME_IN_DESCRIPTION,
	USE_QUICKBOOKS_TERMS,
	USE_CUSTOMER_DEFAULT_TAX,
}

export interface QuickbookSetting {
	id: QuickbooksSettingId;
	name: string;
	checked: boolean;
	disabled: boolean;
}

export interface QuickbookSettingsMutationInput {
	letQBOSetDescription?: boolean;
	letQBOSetInvoiceRef?: boolean;
	useQBOTerms?: boolean;
	useQBODefaultTax?: boolean;
	includeProjectNameInDesc?: boolean;
}
